// ── 엑셀 가져오기 중복 분류 및 실행 계획 순수 로직 ──────
// DB 의존성 및 부수효과 없이 회차 목록과 기존 DB ID를 기반으로 신규/기존 분류 및 가져오기 계획을 계산한다.

import type { ExcelPreviewRow } from '@/pages/admin/components/draw/types';

export type ExcelDuplicateMode = 'skip' | 'overwrite';
export type ExcelRowStatus = 'new' | 'existing';

export interface ClassifiedExcelRow extends ExcelPreviewRow {
  status: ExcelRowStatus;
}

export interface ExcelImportPlan {
  totalCount: number;
  newCount: number;
  existingCount: number;
  targetCount: number;
  mode: ExcelDuplicateMode;
  classifiedRows: ClassifiedExcelRow[];
  targetRows: ExcelPreviewRow[];
  canImport: boolean;
}

/**
 * 엑셀 파싱 행 목록을 기존 DB ID 집합과 대조하여 신규/기존 상태를 부여한다.
 * 원본 preview 배열 및 객체를 변경하지 않고 새 배열/객체를 반환한다.
 */
export function classifyExcelRows(
  rows: readonly ExcelPreviewRow[],
  existingIds: ReadonlySet<number> | readonly number[],
): ClassifiedExcelRow[] {
  const existingSet = existingIds instanceof Set ? existingIds : new Set(existingIds);
  return rows.map((row) => ({
    ...row,
    numbers: [...row.numbers],
    status: existingSet.has(row.id) ? ('existing' as const) : ('new' as const),
  }));
}

/**
 * 엑셀 행과 선택된 중복 모드('skip' | 'overwrite')에 따라 최종 가져오기 계획을 생성한다.
 * - 'skip' 모드: 신규(new) 회차만 targetRows에 포함.
 * - 'overwrite' 모드: 신규 및 기존(existing) 전체 회차를 targetRows에 포함.
 */
export function createExcelImportPlan(
  rows: readonly ExcelPreviewRow[],
  existingIds: ReadonlySet<number> | readonly number[],
  mode: ExcelDuplicateMode = 'skip',
): ExcelImportPlan {
  const classifiedRows = classifyExcelRows(rows, existingIds);
  let newCount = 0;
  let existingCount = 0;

  for (const row of classifiedRows) {
    if (row.status === 'new') {
      newCount++;
    } else {
      existingCount++;
    }
  }

  const targetRows: ExcelPreviewRow[] = [];
  for (const row of classifiedRows) {
    if (mode === 'overwrite' || row.status === 'new') {
      targetRows.push({
        id: row.id,
        draw_date: row.draw_date,
        numbers: [...row.numbers],
        bonus: row.bonus,
        prize_1st: row.prize_1st,
        winners_1st: row.winners_1st,
      });
    }
  }

  return {
    totalCount: rows.length,
    newCount,
    existingCount,
    targetCount: targetRows.length,
    mode,
    classifiedRows,
    targetRows,
    canImport: targetRows.length > 0,
  };
}

/**
 * 실행 결과에 대한 사용자 친화적 성공 메시지를 생성한다.
 */
export function formatExcelImportSuccessMessage({
  mode,
  inserted,
  newCount,
  existingCount,
}: {
  mode: ExcelDuplicateMode;
  inserted: number;
  newCount: number;
  existingCount: number;
}): string {
  if (mode === 'skip') {
    if (existingCount > 0) {
      return `엑셀 가져오기 완료! 신규 ${inserted}건이 등록되었습니다. (기존 ${existingCount}건 건너뜀)`;
    }
    return `엑셀 가져오기 완료! ${inserted}건 모두 등록되었습니다.`;
  }

  // overwrite mode
  if (existingCount > 0) {
    return `엑셀 가져오기 완료! ${inserted}건이 저장되었습니다. (신규 ${newCount}건, 덮어쓰기 ${existingCount}건)`;
  }
  return `엑셀 가져오기 완료! ${inserted}건 모두 등록되었습니다.`;
}
